<template>
  <div class="index py-5">
    <div class="d-flex align-center">
      <span class="d-flex flex-row page-title">{{$t("intranet.aiReports.title")}}</span>
      <v-spacer></v-spacer>
      <v-btn-toggle v-model="status" mandatory dense color="primary">
        <v-btn v-for="value in statuses" :key="value" :value="value" small>
          {{ $t(`intranet.aiReports.status.${value}`) }}
        </v-btn>
      </v-btn-toggle>
      <v-select
        v-model="kind"
        :items="kinds"
        :label="$t('intranet.aiReports.attributes.kind')"
        clearable
        dense
        hide-details
        class="ml-4 kind-filter"
      ></v-select>
    </div>

    <div class="searchable-list-container">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
        show-expand
        single-expand
        :expanded.sync="expanded"
      >
        <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>
        <template v-slot:item.kind="{ item }">{{ $t(`intranet.aiReports.kind.${item.kind}`) }}</template>
        <template v-slot:item.reason="{ item }">
          <v-chip small :color="item.reason === 'other' ? 'grey lighten-2' : 'warning'">
            {{ $t(`intranet.aiReports.reason.${item.reason}`) }}
          </v-chip>
        </template>
        <template v-slot:item.excerpt="{ item }">{{ excerpt(item) }}</template>
        <template v-slot:item.user="{ item }">
          {{ item.user_id || $t('intranet.aiReports.anonymized') }}
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="py-4">
            <div class="report-detail">
              <p v-if="item.comment"><strong>{{ $t('intranet.aiReports.attributes.comment') }} :</strong> {{ item.comment }}</p>

              <template v-if="item.kind === 'sommelier_message'">
                <p v-if="parsed(item).prompt"><strong>{{ $t('intranet.aiReports.prompt') }} :</strong> {{ parsed(item).prompt }}</p>
                <p v-else-if="!item.user_id" class="text--secondary">{{ $t('intranet.aiReports.promptRemoved') }}</p>
                <p><strong>{{ $t('intranet.aiReports.reply') }} :</strong></p>
                <blockquote class="reported">{{ parsed(item).reply }}</blockquote>
                <p class="text--secondary caption">
                  api-data : conversation {{ item.api_data_conversation_id }}, message {{ item.api_data_message_id }} · {{ item.lang }}
                </p>
              </template>

              <template v-else>
                <pre class="reported">{{ pretty(item) }}</pre>
                <p class="text--secondary caption">
                  scan {{ item.ocr_scan_id }} · api-data {{ item.api_data_scan_id }} · {{ item.lang }}
                </p>
              </template>

              <p v-if="item.reviewed_at" class="text--secondary caption">
                {{ $t('intranet.aiReports.reviewedOn', { date: formatDate(item.reviewed_at) }) }}
              </p>

              <v-textarea
                v-model="notes[item.id]"
                :label="$t('intranet.aiReports.attributes.reviewNote')"
                rows="2"
                auto-grow
                outlined
                dense
              ></v-textarea>
              <div class="d-flex">
                <v-btn v-if="item.status !== 'reviewed'" depressed color="success" class="mr-2" :loading="saving" @click="review(item, 'reviewed')">
                  {{ $t('intranet.aiReports.button.reviewed') }}
                </v-btn>
                <v-btn v-if="item.status !== 'dismissed'" depressed class="mr-2" :loading="saving" @click="review(item, 'dismissed')">
                  {{ $t('intranet.aiReports.button.dismissed') }}
                </v-btn>
                <v-btn v-if="item.status !== 'open'" text :loading="saving" @click="review(item, 'open')">
                  {{ $t('intranet.aiReports.button.reopen') }}
                </v-btn>
              </div>
            </div>
          </td>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import AiContentReport from '@/store/models/ai-content-report'
import { errorHandler } from 'aveine-toolbox'

/**
 * Signalements des contenus generes par l'IA (REDMINE-13560, AVN-24).
 *
 * La liste se lit dans la reponse (`save: false`), sans passer par les
 * requetes du store : il n'y a ni relation a resoudre ni formulaire a
 * alimenter, et l'utilisateur n'est qu'un identifiant (absent quand le compte a
 * ete supprime). Le store sert au PATCH, pour sa serialisation.
 */
export default {
  name: 'Index',
  data () {
    return {
      items: [],
      total: 0,
      loading: false,
      saving: false,
      expanded: [],
      notes: {},
      status: 'open',
      kind: null,
      statuses: ['open', 'reviewed', 'dismissed'],
      options: { page: 1, itemsPerPage: 25 },
      APIbaseURL: process.env.VUE_APP_API_HOST
    }
  },
  computed: {
    headers () {
      return [
        { text: this.$t('intranet.aiReports.attributes.createdAt'), value: 'created_at', sortable: false, width: 170 },
        { text: this.$t('intranet.aiReports.attributes.kind'), value: 'kind', sortable: false, width: 150 },
        { text: this.$t('intranet.aiReports.attributes.reason'), value: 'reason', sortable: false, width: 140 },
        { text: this.$t('intranet.aiReports.attributes.content'), value: 'excerpt', sortable: false },
        { text: this.$t('intranet.aiReports.attributes.user'), value: 'user', sortable: false, width: 300 },
        { text: '', value: 'data-table-expand' }
      ]
    },
    kinds () {
      return ['sommelier_message', 'ocr_scan'].map(value => ({ value, text: this.$t(`intranet.aiReports.kind.${value}`) }))
    }
  },
  watch: {
    options: { handler () { this.fetch() }, deep: true },
    status () { this.restart() },
    kind () { this.restart() }
  },
  methods: {
    restart () {
      this.expanded = []
      if (this.options.page !== 1) {
        // Le watcher de `options` relance la recherche.
        this.options = { ...this.options, page: 1 }
      } else {
        this.fetch()
      }
    },

    async fetch () {
      this.loading = true
      try {
        const filter = [`status==${this.status}`]
        if (this.kind) filter.push(`kind==${this.kind}`)
        const response = await AiContentReport.api().get('ai-content-reports', {
          baseURL: this.APIbaseURL,
          save: false,
          params: {
            filter: filter.join(';'),
            // Le plus recent d'abord : c'est la file a traiter.
            sort: '-created_at',
            'page[number]': this.options.page,
            'page[size]': this.options.itemsPerPage
          }
        })
        // Le `transformResponse` global du store a deja converti le document
        // JSON:API : `data` est un tableau de modeles aux attributs en
        // snake_case, une relation devient un objet `{ id }`, et `meta` est
        // pose sur le tableau.
        const models = response.response.data || []
        this.items = (Array.isArray(models) ? models : [models]).map(model => ({
          ...model,
          user_id: model.user ? model.user.id : null
        }))
        this.items.forEach(item => { this.$set(this.notes, item.id, item.review_note || '') })
        this.total = models.meta && models.meta.total ? models.meta.total : this.items.length
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    },

    async review (item, status) {
      this.saving = true
      try {
        // `id` et `$entity_name` : sans eux le serialiseur part sans type ni id
        // (cf. les etiquettes du vin du mois).
        await AiContentReport.api().patch(
          `ai-content-reports/${item.id}`,
          { $entity_name: AiContentReport.entity, id: item.id, status, review_note: this.notes[item.id] || null },
          { baseURL: this.APIbaseURL, save: false }
        )
        this.$toastr.s(this.$t('intranet.aiReports.saved'))
        this.expanded = []
        await this.fetch()
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.saving = false
      }
    },

    parsed (item) {
      try {
        return JSON.parse(item.content || '{}') || {}
      } catch (e) {
        return {}
      }
    },

    pretty (item) {
      return JSON.stringify(this.parsed(item), null, 2)
    },

    excerpt (item) {
      const content = this.parsed(item)
      const text = item.kind === 'sommelier_message'
        ? content.reply
        : JSON.stringify(content.enrichment || content.proposal || {})
      if (!text) return '—'
      return text.length > 120 ? `${text.slice(0, 119)}…` : text
    },

    formatDate (value) {
      if (!value) return '—'
      return new Date(value).toLocaleString(this.$i18n.locale, { dateStyle: 'short', timeStyle: 'short' })
    }
  }
}
</script>

<style scoped>
.kind-filter {
  max-width: 220px;
}
.report-detail {
  max-width: 900px;
}
.reported {
  white-space: pre-wrap;
  border-left: 3px solid #e0e0e0;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 0.9em;
}
</style>
